import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useContext, useState} from "react";

import {StatusBadge} from "@khanacademy/wonder-blocks-badge";
import Button from "@khanacademy/wonder-blocks-button";
import {Text, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    LoadingTable,
    SimpleTable,
    type Column,
} from "@khan/component-simple-table";
import {doNotTranslate, doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import {ColumnName} from "./column-names.ts";
import {EmptyState} from "./empty-state.tsx";
import type {guideTranscripts_aiGuideThreads_threads as ThreadType} from "./guide-transcripts/__graphql-types__/guideTranscripts.ts";
import {GuideTranscriptModal} from "./guide-transcripts/guide-transcript-modal.tsx";
import {countNumberOfInteractions, formatPageInfoString} from "./utils.ts";

import chatCircleIcons from "@phosphor-icons/core/regular/chat-circle.svg";

const DEFAULT_TABLE_PAGE_SIZE = 10;
const TUTOR_ME_TABLE_PAGE_SIZE = 5;

interface Props {
    threads: Array<ThreadType>;
    /**
     * The relationship between the viewer and the profile owner. Used for
     * empty state messaging.
     */
    relationship: "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";
    /**
     * For the conversations view, if a conversation is flagged, render the
     * notice in the first column, under the Chat Name.
     */
    showFlaggedNotice?: boolean;
    /**
     * The columns to display in the table. Defaults to all columns.
     * The values are the keys of the ThreadType object. Order matters.
     */
    visibleColumns?: Array<ColumnName>;
    /**
     * Whether filters are currently applied (non-default values).
     * Used to determine which empty state message to show.
     */
    hasAppliedFilters?: boolean;
    /**
     * Current page number for pagination
     */
    currentPage?: number;
    /**
     * Page change handler for pagination
     */
    onPageChange?: (nextPage: number) => Promise<void> | void;
    /**
     * Whether there are more pages available
     */
    hasMorePages?: boolean;
    /**
     * Whether the current page is the Tutor Me page. Affects the page size.
     */
    isTutorMePage?: boolean;
    /**
     * Whether the result is loading or there is no data.
     */
    isLoadingOrNoData?: boolean;
}

/**
 * Renders a table view of chat threads.
 *
 * @param props - The component props
 * @param props.threads - The list of chat threads to display in the table
 * @param props.showFlaggedNotice - Whether to display a notice for flagged threads (defaults to false)
 * @param props.visibleColumns - The columns to display in the table (defaults to all columns)
 * @param props.relationship - The relationship between the viewer and the profile owner. Used for empty state messaging.
 * @returns A React element containing either a table of threads or an empty state message
 */
export default function TableView({
    threads,
    showFlaggedNotice = false,
    visibleColumns = [
        ColumnName.chatName,
        ColumnName.chatSource,
        ColumnName.flagged,
        ColumnName.messages,
        ColumnName.date,
    ],
    relationship,
    hasAppliedFilters = false,
    currentPage,
    onPageChange,
    hasMorePages = false,
    isTutorMePage = false,
    isLoadingOrNoData = false,
}: Props): React.ReactElement {
    const {entrypoint} = useContext(AppShellContext);
    const isClassroom = entrypoint === "classroom";
    const {pathname} = useLocation();
    const isTutorMe = pathname.includes("/tutor-me");
    const shouldSuppressCTA = isClassroom && isTutorMe;
    const ctaHref = isClassroom ? "/learner/tutor-me" : "learner/class";

    // State to control which modal is open (using thread ID)
    const [openModalThreadId, setOpenModalThreadId] = useState<string | null>(
        null,
    );

    const handleOpenModal = (threadId: string) => {
        setOpenModalThreadId(threadId);
    };

    // Wrap onPageChange to handle both sync and async cases for SimpleTable
    const handlePageChange = async (nextPage: number) => {
        if (onPageChange) {
            const result = onPageChange(nextPage);
            if (result instanceof Promise) {
                await result;
            }
        }
    };

    const caption = t`Chat history`;

    if (isLoadingOrNoData) {
        return (
            <LoadingTable
                numRows={
                    isTutorMePage
                        ? TUTOR_ME_TABLE_PAGE_SIZE
                        : DEFAULT_TABLE_PAGE_SIZE
                }
                columns={getColumns(
                    showFlaggedNotice,
                    visibleColumns,
                    handleOpenModal,
                )}
                caption={caption}
            />
        );
    }

    // Show different empty states based on whether filters are applied
    if (threads.length === 0) {
        if (hasAppliedFilters) {
            // When filters are applied but no results found
            return (
                <EmptyState
                    title={t`No results found`}
                    subtitle={t`Your search didn't match any results. Please try to change the filters.`}
                />
            );
        }

        if (relationship === "SELF") {
            // When no filters are applied and no chats exist (default state)
            return (
                <EmptyState
                    title={t`No chats yet!`}
                    subtitle={t`Once you get started, your chats will show up here.`}
                    href={shouldSuppressCTA ? undefined : ctaHref}
                    buttonText={t`Start your first chat`}
                    showButton={!shouldSuppressCTA}
                />
            );
        }

        // For non-self relationships when no filters are applied
        return (
            <EmptyState
                title={t`All chats have been filtered out`}
                subtitle={t`Change the filters to display your student's progress.`}
            />
        );
    }

    return (
        <View>
            <SimpleTable
                columns={getColumns(
                    showFlaggedNotice,
                    visibleColumns,
                    handleOpenModal,
                )}
                caption={caption}
                data={threads}
                pageSize={
                    isTutorMePage
                        ? TUTOR_ME_TABLE_PAGE_SIZE
                        : DEFAULT_TABLE_PAGE_SIZE
                }
                paginationAfterTable={true}
                onPageChange={onPageChange ? handlePageChange : undefined}
                currentPage={currentPage}
                hasMorePages={hasMorePages}
            />
            {/* Render the modal using ModalLauncher in controlled mode */}
            <ModalLauncher
                opened={openModalThreadId !== null}
                onClose={() => setOpenModalThreadId(null)}
                modal={({closeModal}) => (
                    <GuideTranscriptModal
                        transcriptId={openModalThreadId || ""}
                        relationship="SELF"
                        closeModal={closeModal}
                        systemChatStyle={{
                            backgroundColor: "none",
                        }}
                        userChatStyle={{
                            backgroundColor: "none",
                            border: "1px solid #CBCBCD",
                            color: semanticColor.core.foreground.neutral.strong,
                        }}
                    />
                )}
            />
        </View>
    );
}

/**
 * Generates the columns configuration for a chat history table.
 *
 * The function creates an array of column definitions for displaying chat thread information.
 * Each column contains properties such as label, data key, and cell render functions.
 *
 * @param showFlaggedNotices - Whether to show flagged notices within the chat name column
 * @param visibleColumns - The columns to display in the table
 * @param onOpenModal - Handler to open the modal for a specific thread
 *
 * @returns An array of Column objects configured for the ThreadType data
 *
 * The columns include:
 * - Previous chats (with optional flagged notice)
 * - Source
 * - Flagged status
 * - Messages count
 * - Date (last updated)
 * - Preview action button
 */
function getColumns(
    showFlaggedNotices: boolean,
    visibleColumns: Array<ColumnName>,
    onOpenModal?: (threadId: string) => void,
): Array<Column<ThreadType>> {
    const allColumns: Record<ColumnName, Column<ThreadType>> = {
        chatName: {
            label: doNotTranslate`Previous chats`,
            dataKey: "title",
            cell: (row: ThreadType) => {
                const date = new Date(row.lastUpdatedAt).toLocaleString(
                    RequestInfo.kaLocale,
                    {
                        year: "2-digit",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                    },
                );
                const title = row.title || t`Khanmigo chat from ${date}`;
                return (
                    <>
                        <Tooltip
                            content={
                                row.summary ||
                                doNotTranslateYet`Summary coming soon.`
                            }
                        >
                            <Button
                                onClick={() => {
                                    onOpenModal?.(row.id);
                                }}
                                kind="tertiary"
                            >
                                {title}
                            </Button>
                        </Tooltip>
                        {row.flagged && showFlaggedNotices && (
                            <>
                                <br />
                                <StatusBadge
                                    kind="warning"
                                    styles={{
                                        root: styles.flaggedNotice,
                                        label: styles.flaggedNoticeLabel,
                                    }}
                                    label={t`This chat has been flagged by Khan Academy's moderation system`}
                                />
                            </>
                        )}
                    </>
                );
            },
        },
        chatSource: {
            label: doNotTranslate`Source`,
            cell: (row: ThreadType) => {
                return formatPageInfoString(row);
            },
        },
        flagged: {
            label: doNotTranslate`Flagged`,
            dataKey: "flagged",
            cell: (row: ThreadType) => {
                return row.flagged ? (
                    <StatusBadge kind="critical" label={t`Flagged`} />
                ) : (
                    <Text>&mdash;</Text>
                );
            },
        },
        messages: {
            label: doNotTranslate`Messages`,
            cell: (row: ThreadType) => {
                return (
                    <View style={{alignItems: "center", flexDirection: "row"}}>
                        <PhosphorIcon icon={chatCircleIcons} />
                        <View style={{width: sizing.size_040}} />
                        {countNumberOfInteractions(row)}
                    </View>
                );
            },
        },
        date: {
            label: doNotTranslate`Date`,
            dataKey: "lastUpdatedAt",
            cell: (row: ThreadType) => {
                return new Date(row.lastUpdatedAt).toLocaleString(
                    RequestInfo.kaLocale,
                    {
                        year: "2-digit",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                    },
                );
            },
        },
    };

    const columns: Array<Column<ThreadType>> = [];

    visibleColumns.forEach((column) => {
        if (allColumns[column]) {
            columns.push(allColumns[column]);
        }
    });

    return columns;
}

const styles = StyleSheet.create({
    flaggedNotice: {
        marginBlockStart: sizing.size_080,
    },
    flaggedNoticeLabel: {
        // Disable the default max width of the StatusBadge label.
        // Ideally, badges should be used to display concise information..
        maxWidth: "none",
    },
});
